import { Link } from 'react-router-dom';
import { UtensilsCrossed, Home, Search, Clock, Heart } from 'lucide-react';
import { useStore } from '@/store';
import { useStoreInfo } from '@/lib/store';
import { getFullImageUrl } from '@/lib/utils';
import { motion } from 'framer-motion';

export function Footer() {
  const currentYear = new Date().getFullYear();
  const darkMode = useStore((state) => state.darkMode);
  const { storeInfo } = useStoreInfo();

  const mobileNavItems = [
    { name: 'Home', icon: Home, href: '/home' },
    { name: 'Search', icon: Search, href: '/products' },
    { name: 'Orders', icon: Clock, href: '/track' },
    { name: 'Wishlist', icon: Heart, href: '/wishlist' },
  ];

  return (
    <footer className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>
      {/* Desktop Footer */}
      <div className="hidden md:block border-t dark:border-gray-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-4 gap-8">
            <div>
              <motion.div 
                className="flex items-center space-x-2 mb-6"
                whileHover={{ scale: 1.05 }}
              >
                {storeInfo?.site_logo ? (
                  <img 
                    src={getFullImageUrl(storeInfo.site_logo)}
                    alt={storeInfo.site_name}
                    className="h-8 w-auto"
                  />
                ) : (
                  <>
                    <b className="h-6 w-6 text-brand-primary" />
                    <span className="text-lg font-display font-bold bg-gradient-to-r from-brand-primary to-brand-light bg-clip-text text-transparent">
                      {storeInfo?.site_name || 'Store'}
                    </span>
                  </>
                )}
              </motion.div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {storeInfo?.slogan || 'Your trusted online store'}
              </p>
            </div>

            <div>
              <h3 className="font-display font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/products" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Products
                  </Link>
                </li>
                <li>
                  <Link to="/categories" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Categories
                  </Link>
                </li>
                <li>
                  <Link to="/track" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Track Order
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-display font-semibold mb-4">Help & Support</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/faq" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link to="/return-policy" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Return Policy
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-display font-semibold mb-4">Contact Us</h3>
              {storeInfo?.phone_num && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Phone: {storeInfo.phone_num}
                </p>
              )}
              {storeInfo?.contact_email && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Email: {storeInfo.contact_email}
                </p>
              )}
              {storeInfo?.shop_address && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Address: {storeInfo.shop_address}
                </p>
              )}
            </div>
          </div>

          <div className="border-t dark:border-gray-700 mt-12 pt-8 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              © {currentYear} {storeInfo?.site_name || 'Store'}. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Bar */}
      <motion.div 
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-0 left-0 right-0 md:hidden bg-white dark:bg-gray-800 border-t dark:border-gray-700 z-50"
      >
        <div className="grid grid-cols-4">
          {mobileNavItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className="relative group"
            >
              <motion.div
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.95 }}
                className="flex flex-col items-center justify-center py-3"
              >
                <item.icon className="h-6 w-6 text-gray-500 dark:text-gray-400 group-hover:text-brand-primary dark:group-hover:text-brand-light transition-colors" />
                <span className="text-xs mt-1 text-gray-500 dark:text-gray-400 group-hover:text-brand-primary dark:group-hover:text-brand-light transition-colors">
                  {item.name}
                </span>
                <motion.div
                  className="absolute -bottom-[1px] left-1/2 w-12 h-1 bg-brand-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  layoutId="navIndicator"
                />
              </motion.div>
            </Link>
          ))}
        </div>
      </motion.div>
    </footer>
  );
}

export default Footer;